import type { GuideData } from "@/types/content";
import { regionLetter } from "@/lib/content";
import { SCHOOL_LABELS } from "@/types/navigation";
import type { SchoolId } from "@/types/navigation";

const DOT_COLORS = ["#1E3A35", "#9A552F", "#8C7242"];

type SchematicMapProps = {
  schoolId: SchoolId;
  guide: GuideData;
};

export function SchematicMap({ schoolId, guide }: SchematicMapProps) {
  const campus = guide.overviewMap.campus;
  const regions = guide.overviewMap.regions;
  const positions = [
    { x: "18%", y: "27%" },
    { x: "82%", y: "27%" },
    { x: "68%", y: "86%" },
  ];

  return (
    <div className="relative h-[220px] overflow-hidden rounded-lg border border-[var(--line)] bg-surface-50 p-[18px]">
      <svg
        className="absolute inset-[18px] opacity-40"
        width="100%"
        height="100%"
        viewBox="0 0 320 184"
        preserveAspectRatio="none"
        aria-hidden
      >
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <line
            key={`h${i}`}
            x1="0"
            y1={i * 36 + 2}
            x2="320"
            y2={i * 36 + 2}
            stroke="#DADCE0"
            strokeWidth="0.5"
            strokeDasharray="2 4"
          />
        ))}
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
          <line
            key={`v${i}`}
            x1={i * 32 + 2}
            y1="0"
            x2={i * 32 + 2}
            y2="184"
            stroke="#DADCE0"
            strokeWidth="0.5"
            strokeDasharray="2 4"
          />
        ))}
        <line x1="160" y1="92" x2="60" y2="50" stroke="#229571" strokeWidth="1" strokeDasharray="3 3" />
        <line x1="160" y1="92" x2="260" y2="50" stroke="#229571" strokeWidth="1" strokeDasharray="3 3" />
        <line x1="160" y1="92" x2="220" y2="160" stroke="#229571" strokeWidth="1" strokeDasharray="3 3" />
      </svg>

      <MapMarker x="50%" y="50%" big label={SCHOOL_LABELS[schoolId]} />
      {regions.slice(0, 3).map((r, i) => (
        <MapMarker
          key={r.id}
          x={positions[i]?.x ?? "50%"}
          y={positions[i]?.y ?? "50%"}
          letter={regionLetter(i)}
          name={r.nameCn}
          color={DOT_COLORS[i]}
        />
      ))}

      <span className="absolute bottom-2.5 left-3.5 font-mono text-[10px] tracking-widest text-ink-400">
        SCHEMATIC · NOT TO SCALE
      </span>
      <span className="sr-only">{campus}</span>
    </div>
  );
}

function MapMarker({
  x,
  y,
  big,
  label,
  letter,
  name,
  color = "#1E3A35",
}: {
  x: string;
  y: string;
  big?: boolean;
  label?: string;
  letter?: string;
  name?: string;
  color?: string;
}) {
  return (
    <div
      className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1"
      style={{ left: x, top: y }}
    >
      {big ? (
        <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-forest-900 font-serif text-[11px] font-semibold tracking-wide text-paper shadow-[0_0_0_4px_rgba(30,58,53,0.08),0_4px_10px_rgba(30,58,53,0.15)]">
          {label}
        </div>
      ) : (
        <>
          <div
            className="flex h-[22px] w-[22px] items-center justify-center rounded-full border-[1.5px] border-paper font-mono text-[10px] font-semibold text-paper shadow-sm"
            style={{ background: color }}
          >
            {letter}
          </div>
          {name ? (
            <span className="max-w-[5.5rem] rounded bg-surface-50 px-1 py-px text-center text-[10px] leading-tight text-ink-700 xs:max-w-none xs:whitespace-nowrap xs:text-[10.5px]">
              {name}
            </span>
          ) : null}
        </>
      )}
    </div>
  );
}
